import {
  BuildingLibraryIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  FilmIcon,
  NewspaperIcon,
  PhotoIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

import { BlueprintItemTypes } from "../api/Blueprints";
import { ItemTypes, ThreeSixtyItemTypes } from "../api/Items";
import FamaReportIcon from "../shared-components/icons/FamaReportIcon";
import { sortNamedListAlpha } from "../utils";
import { IconBadgeTooltip } from "./iconBadgeTooltip";

type ItemTypeIconComponent = typeof BuildingLibraryIcon | typeof FamaReportIcon;

export const ITEM_TYPE_LABELS: Record<string, string> = {
  [ThreeSixtyItemTypes.NEWS]: "360 News",
  [ThreeSixtyItemTypes.LEGAL]: "Legal cases",
  [ThreeSixtyItemTypes.SUMMARY]: "Summary",
  [ThreeSixtyItemTypes.FAMA_REPORT]: "Fama Report",
  [ThreeSixtyItemTypes.DILIGENCE]: "Diligence",
  [BlueprintItemTypes.ScrapeInstagramPosts]: "Instagram Posts",
  [BlueprintItemTypes.ScrapeInstagramReels]: "Instagram Reels",
  [ItemTypes.SOCIAL_MEDIA_POST_WITH_CONSENSUS]:
    "Social Media Posts with Consensus",
};

const ITEM_TYPE_ICONS: Record<string, ItemTypeIconComponent> = {
  [ThreeSixtyItemTypes.LEGAL]: BuildingLibraryIcon,
  [ThreeSixtyItemTypes.NEWS]: NewspaperIcon,
  [ThreeSixtyItemTypes.SUMMARY]: DocumentTextIcon,
  [ThreeSixtyItemTypes.FAMA_REPORT]: FamaReportIcon,
  [ThreeSixtyItemTypes.DILIGENCE]: DocumentMagnifyingGlassIcon,
  [BlueprintItemTypes.ScrapeInstagramPosts]: PhotoIcon,
  [BlueprintItemTypes.ScrapeInstagramReels]: FilmIcon,
  [ItemTypes.SOCIAL_MEDIA_POST_WITH_CONSENSUS]: UserGroupIcon,
};

export function itemTypeLabel(itemTypeName: string): string {
  return ITEM_TYPE_LABELS[itemTypeName] ?? itemTypeName;
}

export function ItemTypeIcon({
  itemTypeName,
  className = "h-5 w-5",
}: {
  itemTypeName: string;
  className?: string;
}) {
  const Icon = ITEM_TYPE_ICONS[itemTypeName] ?? DocumentTextIcon;
  return <Icon className={className} aria-hidden="true" />;
}

export function ItemTypeBadge({ itemTypeName }: { itemTypeName: string }) {
  return (
    <IconBadgeTooltip
      label={itemTypeLabel(itemTypeName)}
      className="inline-flex h-7 w-7 items-center justify-center text-gray-600"
    >
      <ItemTypeIcon itemTypeName={itemTypeName} />
    </IconBadgeTooltip>
  );
}

export function ItemTypesCell({ itemTypes }: { itemTypes?: string[] }) {
  const names = sortNamedListAlpha(Array.from(new Set(itemTypes ?? [])));

  if (names.length === 0) {
    return <span className="text-gray-400">None</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {names.map((name) => (
        <ItemTypeBadge key={name} itemTypeName={name} />
      ))}
    </div>
  );
}
